<template>
  <v-navigation-drawer v-model="internalShow" :location="mobile ? 'bottom' : 'right'" name="ai-chat-drawer" order="1" :width="mobile ? undefined : drawerWidth"
    :height="mobile ? '90vh' : undefined" border="s" elevation="4" class="d-flex flex-column" :class="{ 'rounded-t-xl': mobile }">
    <!-- Drag the left edge to resize; double-click to go back to the default width. -->
    <div v-if="!mobile" class="resize-handle" :class="{ 'is-resizing': isResizing }"
      title="Drag to resize, double-click to reset" @pointerdown="startResize" @pointermove="onResizeMove"
      @pointerup="stopResize" @pointercancel="stopResize" @lostpointercapture="stopResize" @dblclick="resetWidth"></div>

    <!-- Floating Close Button -->
    <v-btn
      icon="mdi-close"
      variant="elevated"
      density="comfortable"
      @click="handleClose"
      class="position-absolute"
      style="top: 16px; right: 16px; z-index: 10;"
    ></v-btn>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mx-4 mt-4 mb-0">
      {{ error }}
    </v-alert>

    <!-- Chat Messages Area -->
    <div class="chat-messages pa-4 pt-12 flex-grow-1 overflow-y-auto d-flex flex-column" ref="messagesContainer">
      <div v-for="(msg, index) in messages" :key="index" class="d-flex mb-4"
        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'">
        <v-avatar v-if="msg.role === 'ai'" color="primary" size="36" class="mr-3 mt-1">
          <v-icon color="white" size="20">mdi-robot-outline</v-icon>
        </v-avatar>

        <div class="message-bubble pa-3 rounded-lg"
          :class="msg.role === 'user' ? 'bg-primary text-white user-bubble' : 'ai-bubble ai-reply'">

          <div v-if="msg.role === 'ai'" class="text-body-1 markdown-body" v-html="DOMPurify.sanitize(marked.parse(msg.content))"
            @click="handleMarkdownClick"></div>
          <div v-else class="text-body-1">{{ msg.content }}</div>

          <div v-if="msg.role === 'ai'" class="text-caption text-medium-emphasis disclaimer mt-2">
            <template v-for="(part, partIndex) in messageParts($t('shell_ai_disclaimer'))" :key="partIndex">
              <a v-if="part.slot === 0" href="tel:12267538131">226-753-8131</a>
              <a v-else-if="part.slot === 1" href="mailto:support@readychek.com">support@readychek.com</a>
              <template v-else>{{ part.text }}</template>
            </template>
          </div>

          <div class="d-flex align-center justify-space-between mt-1">
            <div v-if="msg.role === 'ai' && msg.id" class="d-flex align-center">
              <v-btn icon variant="text" size="x-small" :color="msg.liked === 1 ? 'green' : 'grey'"
                @click="rateMessage(msg, 1)">
                <v-icon size="16">{{ msg.liked === 1 ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}</v-icon>
              </v-btn>
              <v-btn icon variant="text" size="x-small" :color="msg.liked === -1 ? 'red' : 'grey'"
                @click="rateMessage(msg, -1)">
                <v-icon size="16">{{ msg.liked === -1 ? 'mdi-thumb-down' : 'mdi-thumb-down-outline' }}</v-icon>
              </v-btn>
            </div>
            <div v-else></div>
            <div class="text-caption text-right" :class="msg.role === 'user' ? 'text-white-opacity-70' : 'text-grey'">
              {{ formatTime(msg.timestamp) }}
            </div>
          </div>

        </div>
      </div>

      <div v-if="isTyping" class="d-flex mb-4 justify-start">
        <v-avatar color="primary" size="36" class="mr-3 mt-1">
          <v-icon color="white" size="20">mdi-robot-outline</v-icon>
        </v-avatar>
        <div class="message-bubble pa-3 rounded-lg ai-bubble d-flex align-center">
          <div class="typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="pa-4 bg-surface border-t flex-shrink-0">
      <v-form @submit.prevent="sendPrompt">
        <v-text-field v-model="newMessage" :placeholder="$t('shell_ai_placeholder')" variant="outlined"
          density="comfortable" hide-details class="rounded-lg">
          <template v-slot:append-inner>
            <v-btn icon="mdi-send" variant="text" color="primary" :disabled="!newMessage.trim() || isTyping"
              @click="sendPrompt" />
          </template>
        </v-text-field>
      </v-form>
    </div>
  </v-navigation-drawer>

  <!-- Image Preview Lightbox -->
  <v-dialog v-model="showImagePreview" max-width="1000" min-height="100">
    <v-card class="bg-black">
      <div class="d-flex justify-end pa-2 position-absolute" style="top: 0; right: 0; z-index: 10;">
        <v-btn icon="mdi-close" variant="flat" color="blue" @click="showImagePreview = false"></v-btn>
      </div>
      <v-img :src="previewImageUrl" max-height="85vh" class="ma-4"></v-img>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import { useDisplay } from 'vuetify';

import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useAiChatService } from '@/v3/composables/useAiChatService';
import { useAiContext } from '@/v3/composables/useAiContext';
import { t } from '@/i18n/index.js';
import { messageParts } from '@/v3/utils/MessageParts.js';

// Configure marked to open links in a new tab
marked.use({
  renderer: {
    link(token) {
      const titleAttr = token.title ? ` title="${token.title}"` : '';
      return `<a href="${token.href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${token.text}</a>`;
    }
  }
});
const props = defineProps({
  modelValue: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue', 'closeDialog']);

const internalShow = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const { mobile } = useDisplay();

// Resizable width (desktop only), remembered in this browser.
const DEFAULT_WIDTH = 500;
const MIN_WIDTH = 360;
const WIDTH_KEY = 'rc.aiChatSidebarWidth';

const clampWidth = (width) => {
  const max = Math.max(MIN_WIDTH, window.innerWidth - 240);
  return Math.round(Math.min(Math.max(width, MIN_WIDTH), max));
};

const savedWidth = () => {
  try {
    const value = parseInt(window.localStorage.getItem(WIDTH_KEY), 10);
    return Number.isFinite(value) ? clampWidth(value) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
};

const drawerWidth = ref(savedWidth());
const isResizing = ref(false);

const saveWidth = () => {
  try {
    window.localStorage.setItem(WIDTH_KEY, String(drawerWidth.value));
  } catch {
    // Storage blocked: the width just is not remembered.
  }
};

const onResizeMove = (event) => {
  if (!isResizing.value) {
    return;
  }
  // The drawer is on the right, so its width is the distance from the pointer to the window's right edge.
  drawerWidth.value = clampWidth(window.innerWidth - event.clientX);
};

const stopResize = () => {
  if (!isResizing.value) {
    return;
  }
  isResizing.value = false;
  document.body.classList.remove('ai-chat-resizing');
  saveWidth();
};

const startResize = (event) => {
  event.preventDefault();
  // Capture keeps the moves and the release coming to the handle even when the
  // pointer leaves the window or passes over the page underneath.
  event.currentTarget.setPointerCapture(event.pointerId);
  isResizing.value = true;
  document.body.classList.add('ai-chat-resizing');
};

const resetWidth = () => {
  drawerWidth.value = clampWidth(DEFAULT_WIDTH);
  saveWidth();
};

// Keep a remembered wide sidebar from covering a window that has since been made smaller.
const onWindowResize = () => {
  drawerWidth.value = clampWidth(drawerWidth.value);
};
window.addEventListener('resize', onWindowResize);

onBeforeUnmount(() => {
  stopResize();
  window.removeEventListener('resize', onWindowResize);
});
const { pageTitle, pageContext } = useAiContext();
const aiService = useAiChatService();

const error = ref(null);
const messages = ref([
  {
    role: 'ai',
    content: t('shell_ai_greeting'),
    timestamp: new Date()
  }
]);

const newMessage = ref('');
const isTyping = ref(false);
const messagesContainer = ref(null);
const showImagePreview = ref(false);
const previewImageUrl = ref('');

const handleMarkdownClick = (e) => {
  if (e.target.tagName === 'IMG') {
    previewImageUrl.value = e.target.src;
    showImagePreview.value = true;
  }
};

const scrollToBottom = async () => {
  await nextTick();
  setTimeout(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTo({
        top: messagesContainer.value.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, 100);
};

const formatTime = (date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const handleClose = () => {
  internalShow.value = false;
  emit('closeDialog');
};

const rateMessage = (msg, likedValue) => {
  if (msg.liked === likedValue) return; // Ignore if already selected
  msg.liked = likedValue;
  savePrompt(msg, likedValue);
};

const savePrompt = async (message, liked) => {
  aiService.savePrompt(message.id, liked);
}

const sendPrompt = async () => {
  if (!newMessage.value.trim() || isTyping.value) return;

  const userText = newMessage.value.trim();
  messages.value.push({
    role: 'user',
    content: userText,
    timestamp: new Date()
  });

  newMessage.value = '';
  scrollToBottom();

  // Simulate AI response
  isTyping.value = true;
  scrollToBottom();

  const params = {
    prompt: userText,
    pageName: pageTitle.value,
    pageContext: pageContext.value
  };

  aiService.sendPrompt(
    params,
    (response) => {
      messages.value.push({
        role: 'ai',
        id: response.id,
        content: response.content,
        timestamp: new Date(),
        liked: null,
      });
    },
    (errorMessage) => {
      messages.value.push({
        role: 'ai',
        content: errorMessage,
        timestamp: new Date()
      });
    },
    () => {
      isTyping.value = false;
      scrollToBottom();
    }
  );
};

onMounted(() => {
  scrollToBottom();
});
</script>

<style scoped>

/* Vuetify wraps the drawer's slot content in .v-navigation-drawer__content, so the
   flex-column on the drawer itself never reaches these children. Without this the
   messages area grows to fit the conversation instead of scrolling, pushing the
   input area out of the drawer once an answer is long enough. */
:deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 8px;
  margin-left: -4px;
  cursor: col-resize;
  z-index: 20;
  touch-action: none;
}

.resize-handle:hover,
.resize-handle.is-resizing {
  background-color: rgba(25, 118, 210, 0.35);
}

/* The bubble is a flex item; without min-width: 0 it grows to fit its widest
   child (a table or a long link) and pushes past the sidebar. */
.message-bubble {
  min-width: 0;
  overflow-wrap: anywhere;
}

/* Colours come from the Vuetify theme so the sidebar follows the dark mode setting. */
.chat-messages {
  background-color: rgba(var(--v-theme-on-surface), 0.03);
  /* flex items default to min-height: auto, which would let this grow past the drawer */
  min-height: 0;
}

.user-bubble {
  border-bottom-right-radius: 4px !important;
  max-width: 85%;
}

/* Axle's answers use the full width beside the avatar; the user's own messages
   stay narrower on the right so the two sides still read apart. */
.ai-reply {
  flex: 1 1 auto;
}

.ai-bubble {
  border-bottom-left-radius: 4px !important;
  background-color: rgba(var(--v-theme-on-surface), 0.06);
  color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Typing Indicator Animation */
.typing-indicator {
  display: flex;
  align-items: center;
  height: 20px;
}

.typing-indicator span {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #90a4ae;
  border-radius: 50%;
  margin: 0 2px;
  animation: typing 1.4s infinite both;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {

  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.disclaimer {
  line-height: 1.3;
}

.disclaimer a {
  color: inherit;
  text-decoration: underline;
}

/* Markdown Styles */
.markdown-body :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-body :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-body :deep(a) {
  color: rgb(var(--v-theme-primary));
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.markdown-body :deep(code) {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: monospace;
}

.markdown-body :deep(pre) {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
  padding: 0.8rem;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 0.5rem;
}

/* Tables scroll sideways inside the bubble instead of widening it. */
.markdown-body :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding: 0.25rem 0.5rem;
  text-align: left;
  vertical-align: top;
  overflow-wrap: normal;
}

.markdown-body :deep(th) {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
}

.markdown-body :deep(img) {
  max-width: 320px;
  height: auto;
  border-radius: 8px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.markdown-body :deep(img:hover) {
  transform: scale(1.05);
}
</style>

<style>
/* While dragging the sidebar edge: no text selection, and the drawer follows the
   pointer instead of animating to each new width. */
body.ai-chat-resizing {
  user-select: none;
  cursor: col-resize;
}

body.ai-chat-resizing .v-navigation-drawer,
body.ai-chat-resizing .v-main {
  transition: none !important;
}
</style>
